import type {
  BillingInterval,
  BillingPlanCatalogItem,
  BillingPlanKey,
  BillingSummary,
  BillingState,
  EntitlementGate,
  EntitlementGateResult,
  EntitlementOverage,
  EntitlementSnapshot,
  PaidPeriodBasis,
  PilotAccessGrant,
  StoreBillingSubscription,
  BillingUsagePeriod,
} from '../types/billing';
import {
  BILLING_CANCELLABLE_STATES,
  BILLING_CURRENT_PLAN_STATES,
  BILLING_PLAN_RANK,
} from '../types/billing';

function timestamp(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function hasPaidActionWindow(input: {
  readonly state: BillingState;
  readonly nowIso: string;
  readonly paidThroughAt: string | null;
  readonly graceEndsAt: string | null;
  readonly latestPaidBasis: PaidPeriodBasis | null;
}): boolean {
  const now = timestamp(input.nowIso);
  if (now === null || !input.latestPaidBasis) return false;
  const basisEnd = timestamp(input.latestPaidBasis.periodEnd);
  if (basisEnd === null) return false;

  if (input.state === 'active' || input.state === 'cancelled_pending_period_end') {
    const paidThrough = timestamp(input.paidThroughAt);
    return paidThrough !== null && paidThrough > now && basisEnd >= paidThrough;
  }

  if (input.state === 'grace') {
    const graceEnds = timestamp(input.graceEndsAt);
    return graceEnds !== null && graceEnds > now && basisEnd <= now;
  }

  return false;
}

export function hasPilotAccessWindow(input: {
  readonly nowIso: string;
  readonly grant: PilotAccessGrant | null;
}): boolean {
  const now = timestamp(input.nowIso);
  if (now === null || !input.grant || input.grant.revokedAt) return false;
  const startsAt = timestamp(input.grant.accessStartsAt);
  const endsAt = timestamp(input.grant.accessEndsAt);
  return startsAt !== null && endsAt !== null && startsAt <= now && endsAt > now;
}

function overages(input: {
  readonly activeProductsUsed: number;
  readonly activeProductLimit: number;
  readonly mediaStorageBytesUsed: number;
  readonly mediaStorageBytes: number;
  readonly aiImageEditsUsed: number;
  readonly aiImageEditsPerPeriod: number;
}): readonly EntitlementOverage[] {
  const items: EntitlementOverage[] = [];
  if (input.activeProductsUsed > input.activeProductLimit) {
    items.push({
      gate: 'active_products',
      used: input.activeProductsUsed,
      limit: input.activeProductLimit,
    });
  }
  if (input.mediaStorageBytesUsed > input.mediaStorageBytes) {
    items.push({
      gate: 'media_storage',
      used: input.mediaStorageBytesUsed,
      limit: input.mediaStorageBytes,
    });
  }
  if (input.aiImageEditsUsed > input.aiImageEditsPerPeriod) {
    items.push({
      gate: 'ai_image_edits',
      used: input.aiImageEditsUsed,
      limit: input.aiImageEditsPerPeriod,
    });
  }
  return items;
}

export function deriveEntitlementSnapshot(input: {
  readonly storeId: string;
  readonly nowIso: string;
  readonly subscription: StoreBillingSubscription | null;
  readonly plan: BillingPlanCatalogItem | null;
  readonly pilotGrant?: PilotAccessGrant | null;
  readonly pilotPlan?: BillingPlanCatalogItem | null;
  readonly latestPaidBasis: PaidPeriodBasis | null;
  readonly activeProductsUsed: number;
  readonly mediaStorageBytesUsed: number;
  readonly usage: BillingUsagePeriod | null;
}): EntitlementSnapshot {
  const state = input.subscription?.state ?? 'missing';
  const paypalPaid =
    !!input.subscription &&
    !!input.plan &&
    input.subscription.planKey === input.plan.planKey &&
    input.subscription.interval === input.plan.interval &&
    input.subscription.paypalEnvironment === input.plan.paypalEnvironment &&
    input.subscription.paypalPlanId === input.plan.paypalPlanId &&
    hasPaidActionWindow({
      state,
      nowIso: input.nowIso,
      paidThroughAt: input.subscription.paidThroughAt,
      graceEndsAt: input.subscription.graceEndsAt,
      latestPaidBasis: input.latestPaidBasis,
    });
  const pilotPaid =
    !paypalPaid &&
    !!input.pilotPlan &&
    input.pilotGrant?.planKey === input.pilotPlan.planKey &&
    hasPilotAccessWindow({
      nowIso: input.nowIso,
      grant: input.pilotGrant ?? null,
    });
  const paid = paypalPaid || pilotPaid;
  const activePlan = paypalPaid ? input.plan : pilotPaid ? input.pilotPlan : null;

  const activeProductLimit = activePlan ? activePlan.activeProductLimit : 0;
  const mediaStorageBytes = activePlan ? activePlan.mediaStorageBytes : 0;
  const aiImageEditsPerPeriod = activePlan ? activePlan.aiImageEditsPerPeriod : 0;
  const aiImageEditsUsed = input.usage?.aiImageEditsUsed ?? 0;

  return {
    storeId: input.storeId,
    state: pilotPaid ? 'active' : state,
    planKey: paypalPaid
      ? (input.subscription?.planKey ?? null)
      : pilotPaid
        ? input.pilotPlan.planKey
        : (input.subscription?.planKey ?? input.pilotGrant?.planKey ?? null),
    interval: paypalPaid
      ? (input.subscription?.interval ?? null)
      : pilotPaid
        ? input.pilotPlan.interval
        : (input.subscription?.interval ?? null),
    paidAccessUntil: paypalPaid
      ? (input.subscription?.paidThroughAt ?? null)
      : pilotPaid
        ? input.pilotGrant.accessEndsAt
        : (input.subscription?.paidThroughAt ?? null),
    graceEndsAt: input.subscription?.graceEndsAt ?? null,
    hasVerifiedPaidBasis: !!input.latestPaidBasis,
    hasActivePilotAccess: pilotPaid,
    accessSource: paypalPaid ? 'paypal' : pilotPaid ? 'pilot' : 'none',
    pilotAccessUntil: input.pilotGrant?.accessEndsAt ?? null,
    canUsePaidActions: paid,
    activeProductLimit,
    activeProductsUsed: input.activeProductsUsed,
    mediaStorageBytes,
    mediaStorageBytesUsed: input.mediaStorageBytesUsed,
    aiImageEditsPerPeriod,
    aiImageEditsUsed,
    promotionToolsLevel: activePlan ? activePlan.promotionToolsLevel : null,
    analyticsLevel: activePlan ? activePlan.analyticsLevel : null,
    supportLevel: activePlan ? activePlan.supportLevel : null,
    overages: overages({
      activeProductsUsed: input.activeProductsUsed,
      activeProductLimit,
      mediaStorageBytesUsed: input.mediaStorageBytesUsed,
      mediaStorageBytes,
      aiImageEditsUsed,
      aiImageEditsPerPeriod,
    }),
  };
}

function blockedForState(snapshot: EntitlementSnapshot): EntitlementGateResult {
  if (snapshot.state === 'missing') {
    return {
      allowed: false,
      reason: 'missing_subscription',
      messageKey: 'admin.billing.status.missing',
      upgradeTargetPlanKey: 'starter',
    };
  }
  if (snapshot.state === 'configuration_error') {
    return {
      allowed: false,
      reason: 'configuration_error',
      messageKey: 'admin.billing.errors.configurationMissing',
      upgradeTargetPlanKey: null,
    };
  }
  if (snapshot.state === 'approval_pending') {
    return {
      allowed: false,
      reason: 'activation_pending',
      messageKey: 'admin.billing.errors.activationPending',
      upgradeTargetPlanKey: null,
    };
  }
  if (snapshot.state === 'suspended') {
    return {
      allowed: false,
      reason: 'suspended',
      messageKey: 'admin.billing.status.suspended',
      upgradeTargetPlanKey: null,
    };
  }
  if (snapshot.state === 'expired') {
    return {
      allowed: false,
      reason: 'expired',
      messageKey: 'admin.billing.status.expired',
      upgradeTargetPlanKey: 'starter',
    };
  }
  if (snapshot.state === 'cancelled') {
    return {
      allowed: false,
      reason: 'cancelled',
      messageKey: 'admin.billing.status.cancelled',
      upgradeTargetPlanKey: 'starter',
    };
  }
  return {
    allowed: false,
    reason: 'payment_required',
    messageKey: 'admin.billing.warning.limited',
    upgradeTargetPlanKey: snapshot.planKey ?? 'starter',
  };
}

export function canUseEntitlementGate(
  snapshot: EntitlementSnapshot,
  gate: EntitlementGate,
  delta: { readonly bytesIncrease?: number; readonly activeProductIncrease?: number } = {},
): EntitlementGateResult {
  if (gate === 'promotion_link_basic_share') {
    return { allowed: true, reason: null, messageKey: null, upgradeTargetPlanKey: null };
  }

  if (!snapshot.canUsePaidActions) return blockedForState(snapshot);

  if (
    (gate === 'active_product_create' || gate === 'active_product_publish') &&
    snapshot.activeProductsUsed + (delta.activeProductIncrease ?? 1) > snapshot.activeProductLimit
  ) {
    return {
      allowed: false,
      reason: 'product_limit_reached',
      messageKey: 'admin.entitlements.productLimitBlocked',
      upgradeTargetPlanKey: snapshot.planKey === 'pro' ? null : 'growth',
    };
  }

  if (
    gate === 'media_storage_increase' &&
    snapshot.mediaStorageBytesUsed + (delta.bytesIncrease ?? 0) > snapshot.mediaStorageBytes
  ) {
    return {
      allowed: false,
      reason: 'storage_limit_reached',
      messageKey: 'admin.entitlements.storageLimitBlocked',
      upgradeTargetPlanKey: snapshot.planKey === 'pro' ? null : 'growth',
    };
  }

  if (gate === 'ai_image_edit' && snapshot.aiImageEditsUsed >= snapshot.aiImageEditsPerPeriod) {
    return {
      allowed: false,
      reason: 'ai_quota_exhausted',
      messageKey: 'admin.entitlements.aiQuotaBlocked',
      upgradeTargetPlanKey: snapshot.planKey === 'pro' ? null : 'growth',
    };
  }

  if (
    (gate === 'promotion_kit_paid_tools' ||
      gate === 'promotion_analytics' ||
      gate === 'advanced_analytics') &&
    snapshot.promotionToolsLevel !== 'full'
  ) {
    return {
      allowed: false,
      reason: 'plan_upgrade_required',
      messageKey: 'admin.entitlements.promotionKitBlocked',
      upgradeTargetPlanKey: 'growth',
    };
  }

  if (gate === 'priority_support_indicator' && snapshot.supportLevel !== 'priority') {
    return {
      allowed: false,
      reason: 'plan_upgrade_required',
      messageKey: 'admin.entitlements.upgradePrompt.description',
      upgradeTargetPlanKey: 'pro',
    };
  }

  return { allowed: true, reason: null, messageKey: null, upgradeTargetPlanKey: null };
}

export function compareBillingPlans(a: BillingPlanKey, b: BillingPlanKey): -1 | 0 | 1 {
  const diff = BILLING_PLAN_RANK[a] - BILLING_PLAN_RANK[b];
  if (diff < 0) return -1;
  if (diff > 0) return 1;
  return 0;
}

export type BillingPlanChangeKind =
  | 'subscribe'
  | 'current'
  | 'upgrade'
  | 'downgrade'
  | 'interval_change'
  | 'continue_approval';

export interface BillingPlanChangeInput {
  readonly currentPlanKey: BillingPlanKey | null;
  readonly currentInterval: BillingInterval | null;
  readonly targetPlanKey: BillingPlanKey;
  readonly targetInterval: BillingInterval;
  readonly pendingApprovalPlanKey?: BillingPlanKey | null;
  readonly pendingApprovalInterval?: BillingInterval | null;
}

export function getBillingPlanChangeKind(input: BillingPlanChangeInput): BillingPlanChangeKind {
  if (
    input.targetPlanKey === input.pendingApprovalPlanKey &&
    input.targetInterval === input.pendingApprovalInterval
  ) {
    return 'continue_approval';
  }

  if (
    input.targetPlanKey === input.currentPlanKey &&
    input.targetInterval === input.currentInterval
  ) {
    return 'current';
  }

  if (!input.currentPlanKey) return 'subscribe';

  const planComparison = compareBillingPlans(input.currentPlanKey, input.targetPlanKey);
  if (planComparison < 0) return 'upgrade';
  if (planComparison > 0) return 'downgrade';
  return 'interval_change';
}

export interface BillingSubscriptionUiState {
  readonly currentPlanKey: BillingPlanKey | null;
  readonly currentInterval: BillingInterval | null;
  readonly isRevisable: boolean;
  readonly canCancel: boolean;
}

const CURRENT_PLAN_STATES: ReadonlySet<BillingState> = new Set(BILLING_CURRENT_PLAN_STATES);
const CANCELLABLE_STATES: ReadonlySet<BillingState> = new Set(BILLING_CANCELLABLE_STATES);

export function getBillingSubscriptionUiState(summary: BillingSummary): BillingSubscriptionUiState {
  const subscription = summary.subscription;
  const showsCurrentPlan =
    !!subscription?.planKey &&
    !!subscription.interval &&
    CURRENT_PLAN_STATES.has(subscription.state) &&
    summary.entitlement.hasVerifiedPaidBasis;

  return {
    currentPlanKey: showsCurrentPlan ? subscription.planKey : null,
    currentInterval: showsCurrentPlan ? subscription.interval : null,
    isRevisable: showsCurrentPlan,
    canCancel: !!subscription?.paypalSubscriptionId && CANCELLABLE_STATES.has(subscription.state),
  };
}

export function summarizeDowngradeOverages(
  snapshot: EntitlementSnapshot,
  targetPlan: BillingPlanCatalogItem,
): readonly EntitlementOverage[] {
  return overages({
    activeProductsUsed: snapshot.activeProductsUsed,
    activeProductLimit: targetPlan.activeProductLimit,
    mediaStorageBytesUsed: snapshot.mediaStorageBytesUsed,
    mediaStorageBytes: targetPlan.mediaStorageBytes,
    aiImageEditsUsed: snapshot.aiImageEditsUsed,
    aiImageEditsPerPeriod: targetPlan.aiImageEditsPerPeriod,
  });
}
